import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { X, Download, FileText } from 'lucide-react';
import Sidebar from "../../../component/includes/sidebar";
import { CreateServicePublicData, ServicePublicContent, servicePublicContentService, UpdateServicePublicData } from "../../../services/public";

const PublicContentManagement = () => {
  // State Management
  const [publicContents, setPublicContents] = useState<ServicePublicContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState<ServicePublicContent | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [viewerFile, setViewerFile] = useState<string | null>(null);
  const [newContent, setNewContent] = useState<CreateServicePublicData>({
    title: "",
    description: "",
    file: {} as File,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // File Handling Functions
  const handleFileDownload = (file?: string | File) => {
    if (!file) {
      showError('No file available for download');
      return;
    }

    // Construct the full file URL
    const fileUrl = `${process.env.REACT_APP_API_URL}/${file}`;

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', typeof file === 'string' ? file : file.name);
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
  }

  const handleShowFile = (file?: string | File) => {
    if (!file) {
      showError('No file available to show');
      return;
    }

    // Construct the full file URL
    const fileUrl = `${process.env.REACT_APP_API_URL}/${file}`;
    setViewerFile(fileUrl);
  }

  const closeFileViewer = () => {
    setViewerFile(null);
  }

  // File Viewer Modal Component
  const FileViewerModal = () => {
    if (!viewerFile) return null;

    // Determine file type
    const fileExtension = viewerFile.split('.').pop()?.toLowerCase();
    const isPDF = fileExtension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension || '');

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] p-6">
          {/* Close Button */}
          <button 
            onClick={closeFileViewer}
            className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
          >
            <X size={24} />
          </button>

          {/* File Viewer Content */}
          <div className="w-full h-full flex flex-col items-center">
            {isPDF && (
              <iframe 
                src={viewerFile} 
                className="w-full h-[70vh]" 
                title="PDF Viewer"
              />
            )}

            {isImage && (
              <img 
                src={viewerFile} 
                alt="Uploaded file" 
                className="max-w-full max-h-[70vh] object-contain" 
              />
            )}

            {!isPDF && !isImage && (
              <div className="flex flex-col items-center justify-center h-full">
                <FileText size={64} className="text-gray-400 mb-4" />
                <p className="text-gray-600">File preview not available</p>
              </div>
            )}

            {/* Download Button */}
            <button 
              onClick={() => handleFileDownload(viewerFile)}
              className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded inline-flex items-center"
            >
              <Download size={20} className="mr-2" />
              Download File
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Data Fetching
  const fetchPublicContents = async () => {
    try {
      setLoading(true);
      const response = await servicePublicContentService.getServicePublicContent();
      setPublicContents(response);
    } catch (err) {
      showError("Failed to fetch public contents");
      console.error("Error fetching public contents:", err);
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  };

  useEffect(() => {
    fetchPublicContents();
  }, []);

  // Utility Functions
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 3000);
  };

  const resetForm = () => {
    setNewContent({ title: "", description: "", file: {} as File });
    setEditingContent(null);
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  // Enhanced Loading Component
  const LoadingSpinner = () => (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-[9999]">
      <div className="flex flex-col items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 border-solid mb-4"></div>
        <p className="text-gray-600 text-lg">Loading public contents...</p>
      </div>
    </div>
  );

  // CRUD Operations
  const handleCreateContent = async () => {
    if (!newContent.title.trim() || !newContent.description.trim() || !selectedFile) {
      showError("Title, description, and file are required");
      return;
    }

    setLoading(true);
    try {
      const contentData: CreateServicePublicData = {
        title: newContent.title,
        description: newContent.description,
        file: selectedFile,
      };
      await servicePublicContentService.createServicePublicContent(contentData);
      await fetchPublicContents();
      showSuccess("Public content created successfully");
      resetForm();
    } catch (err) {
      showError("Failed to create public content");
      console.error("Error creating public content:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateContent = async () => {
    if (
      !editingContent?.title.trim() ||
      !editingContent?.description.trim()
    ) {
      showError("Title and description cannot be empty");
      return;
    }

    setLoading(true);
    try {
      const updatedData: UpdateServicePublicData = {
        id: editingContent.id,
        title: editingContent.title,
        description: editingContent.description,
        file: selectedFile || undefined,
      };
      await servicePublicContentService.updateServicePublicContent(updatedData);
      await fetchPublicContents();
      showSuccess("Public content updated successfully");
      resetForm();
    } catch (err) {
      showError("Failed to update public content");
      console.error("Error updating public content:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContent = async (contentId: number) => {
    if (!window.confirm("Are you sure you want to delete this public content?"))
      return;

    setLoading(true);
    try {
      await servicePublicContentService.deleteServicePublicContent(contentId);
      await fetchPublicContents();
      showSuccess("Public content deleted successfully");
    } catch (err) {
      showError("Failed to delete public content");
      console.error("Error deleting public content:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filtered Data
  const filteredPublicContents = publicContents.filter(
    (content) =>
      content.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      content.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Page render with full page loading
  if (pageLoading) {
    return <LoadingSpinner />;
  }

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ script: "sub" }, { script: "super" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["link", "image"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ["clean"],
    ],
  };
  function stripHtmlTags(content: string) {
    const doc = new DOMParser().parseFromString(content, 'text/html');
    return doc.body.textContent || "";
  }

  const formats = [
    "header", "bold", "italic", "underline", "strike",
    "list", "bullet", "script", "indent", 
    "link", "image", "color", "background", "align"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-[9999]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      )}

      {/* Notification Messages */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Success!</strong>
            <p className="block sm:inline ml-2">{successMessage}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <strong className="font-bold">Error!</strong>
            <p className="block sm:inline ml-2">{error}</p>
          </div>
        )}
      </div>
      <div className="flex flex-col md:flex-row">
        <Sidebar />

        <main className="flex-1 p-4 md:p-6 mt-10">
          <div className="max-w-8xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                Public Content Management
              </h1>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full md:w-auto bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
              >
                Add New Content
              </button>
            </div>

            <input
              type="text"
              placeholder="Search public contents..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />

            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left">ID</th>
                      <th className="px-4 py-3 text-left">Title</th>
                      <th className="px-4 py-3 text-left">Description</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">File</th>
                      <th className="px-4 py-3 text-left hidden md:table-cell">Created At</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPublicContents.map((content) => (
                      <tr
                        key={content.id}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="px-4 py-3">{content.id}</td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs md:max-w-md truncate">
                            {content.title}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="max-w-xs md:max-w-md truncate">
                          {stripHtmlTags(content.description)}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {content.file && (
                            <div className="flex space-x-2">
                              <button 
                                onClick={() => handleShowFile(content.file)}
                                className="text-green-500 hover:underline flex items-center"
                              >
                                <FileText size={16} className="mr-1" /> Show
                              </button>
                              <button 
                                onClick={() => handleFileDownload(content.file)}
                                className="text-blue-500 hover:underline flex items-center"
                              >
                                <Download size={16} className="mr-1" /> Download
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          {new Date(content.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditingContent(content);
                                setIsModalOpen(true);
                              }}
                              className="text-blue-500 hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteContent(content.id)}
                              className="text-red-500 hover:underline"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {editingContent ? "Edit Public Content" : "Add New Public Content"}
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <input
                  type="text"
                  value={
                    editingContent ? editingContent.title : newContent.title
                  }
                  onChange={(e) => {
                    if (editingContent) {
                      setEditingContent({
                        ...editingContent,
                        title: e.target.value,
                      });
                    } else {
                      setNewContent({ ...newContent, title: e.target.value });
                    }
                  }}
                  placeholder="Enter content title..."
                  className="border px-4 py-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    setSelectedFile(file || null);
                  }}
                  className="border px-4 py-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <ReactQuill
                  value={
                    editingContent
                      ? editingContent.description
                      : newContent.description
                  }
                  onChange={(value: string) => {
                    if (editingContent) {
                      setEditingContent({
                        ...editingContent,
                        description: value,
                      });
                    } else {
                        setNewContent({ ...newContent, description: value });
                    }
                  }}
                  modules={modules}
                  formats={formats}
                  placeholder="Enter content description..."
                  className="h-60 mb-12"
                />
                <div className="flex flex-col md:flex-row justify-end gap-4 mt-20">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="w-full md:w-auto bg-gray-300 text-gray-800 px-6 py-3 rounded-lg hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={
                      editingContent
                        ? handleUpdateContent
                        : handleCreateContent
                    }
                    className="w-full md:w-auto bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    {editingContent ? "Update" : "Create"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicContentManagement;